/**
 * Pacxa brand ramp for Fluent UI v9 (Fluent 2).
 *
 * Fluent themes are generated from a 16-step `BrandVariants` ramp; every
 * brand-derived token (`colorBrandBackground`, `colorCompoundBrandStroke`, the
 * hover/pressed variants, focus rings, selected-nav fills…) is read off that
 * ramp, so getting the ramp right is the whole job.
 *
 * Derivation
 * ----------
 * The Nexora primary is `hsl(205 85% 40%)` = `#0F74BD`.
 *
 * Microsoft's stock web brand ("Fluent blue") is `#0F6CBD` at shade 80, which
 * is `hsl(207.93 85.3% 40%)` — the same saturation and lightness as ours, three
 * degrees further round the wheel. So rather than invent a ramp, this one is
 * Microsoft's ramp with a constant hue rotation of −2.93° applied to every
 * shade, preserving each step's saturation, lightness and the slight hue drift
 * the ramp has toward its light end. The result is perceptually identical in
 * shape to stock Fluent (contrast ratios between shades are unchanged, so
 * Fluent's accessibility guarantees carry over) but lands on the Pacxa blue.
 *
 * Shade 80 is `colorBrandBackground` in the light theme and is pinned to the
 * exact Nexora hex; shade 100 is `colorBrandBackground` in the dark theme and
 * comes out at `hsl(207 90% 62%)`, which is the Nexora dark primary
 * (`hsl(209 89% 62%)`) to within a rounding step.
 *
 * Nexora shape and elevation
 * --------------------------
 * The brand ramp only gets the colour right. The rest of the Nexora identity —
 * the 10px card corner, the soft two-layer shadow, the Segoe UI Variable stack —
 * lives in Fluent's radius, shadow and font tokens, which every Fluent component
 * already reads. Overriding them here means the look lands on `Card`, `Button`,
 * `Input`, `MessageBar`, `Dialog`, `Menu` and everything else without a single
 * component being restyled at the call site. See `applyNexoraLook` below.
 */
import { createDarkTheme, createLightTheme, } from '@fluentui/react-components';
export const pacxaBrand = {
    10: '#061724',
    20: '#082338',
    30: '#0A2E4A',
    40: '#0C3B5E',
    50: '#0E4775',
    60: '#0F548C',
    70: '#115EA3',
    // The prototype's `--brand`. This is Fluent's own stock web brand, so the
    // ramp below is Microsoft's unmodified — see the note at the top of the file.
    80: '#0F6CBD',
    90: '#2886DE',
    100: '#479EF5',
    110: '#62ABF5',
    120: '#77B7F7',
    130: '#96C6FA',
    140: '#B4D6FA',
    150: '#CFE4FA',
    160: '#EBF3FC',
};
/**
 * Nexora's font stack, from `.nx-app` in `nexora-ui.css`. Identical to Fluent's
 * own default except for the leading `Segoe UI Variable`, the variable-axis
 * version of Segoe that ships with Windows 11 — which is what gives Nexora its
 * slightly tighter, more optical headings on the machines this ERP runs on.
 */
const fontFamilyBase = '"Segoe UI Variable", "Segoe UI", -apple-system, BlinkMacSystemFont, system-ui, Roboto, "Helvetica Neue", sans-serif';
/**
 * Nexora corner radii.
 *
 * `borderRadiusMedium` is the one that matters: Fluent reads it for cards,
 * inputs, menus, badges and buttons, and Nexora's signature is the 10px card.
 * `Large`/`XLarge` are pushed above it so the scale stays monotonic — leaving
 * them at Fluent's 6px/8px would make dialogs and popovers *less* rounded than
 * the cards inside them, which reads as a bug rather than a style.
 */
const nexoraRadius = {
    borderRadiusSmall: '4px',
    borderRadiusMedium: '8px',
    borderRadiusLarge: '12px',
    borderRadiusXLarge: '14px',
};
/**
 * Nexora elevation.
 *
 * Two layers: a 1px contact shadow that seats the card on the page, and a wide,
 * very soft ambient shadow. `shadow4` is the exact Nexora card value
 * (`0 1px 2px rgba(0,0,0,.04), 0 6px 16px rgba(0,0,0,.06)`) because that is the
 * token Fluent's `Card` uses; the rest of the ramp is scaled from it so
 * popovers, dialogs and menus stay in the same family.
 *
 * The `*Brand` variants carry Nexora's blue-tinted hover shadow
 * (`0 4px 16px rgba(15,108,189,.10)`); `shadow4Brand` is what `cardInteractive`
 * and `kpi` raise to on hover.
 */
const lightShadows = {
    shadow2: '0 1px 2px rgba(0,0,0,.04), 0 2px 6px rgba(0,0,0,.05)',
    shadow4: '0 1px 2px rgba(0,0,0,.05), 0 2px 8px rgba(0,0,0,.04)',
    shadow8: '0 2px 4px rgba(0,0,0,.05), 0 10px 24px rgba(0,0,0,.08)',
    shadow16: '0 3px 6px rgba(0,0,0,.06), 0 16px 36px rgba(0,0,0,.10)',
    shadow28: '0 5px 10px rgba(0,0,0,.07), 0 24px 56px rgba(0,0,0,.13)',
    shadow64: '0 8px 16px rgba(0,0,0,.08), 0 44px 96px rgba(0,0,0,.16)',
    shadow2Brand: '0 1px 2px rgba(15,108,189,.06), 0 2px 8px rgba(15,108,189,.08)',
    shadow4Brand: '0 1px 2px rgba(15,108,189,.06), 0 4px 16px rgba(15,108,189,.10)',
    shadow8Brand: '0 2px 6px rgba(15,108,189,.08), 0 8px 24px rgba(15,108,189,.14)',
    shadow16Brand: '0 3px 8px rgba(15,108,189,.10), 0 16px 36px rgba(15,108,189,.18)',
    shadow28Brand: '0 5px 12px rgba(15,108,189,.12), 0 24px 56px rgba(15,108,189,.22)',
    shadow64Brand: '0 8px 18px rgba(15,108,189,.14), 0 44px 96px rgba(15,108,189,.26)',
};
/**
 * Dark-mode elevation is *not* the light values reused.
 *
 * A 4%-black shadow is invisible against a #292929 surface, so on dark the
 * geometry is kept and the alphas are raised roughly five-fold — which is how
 * Fluent's own dark theme differs from its light one. The brand-tinted hovers
 * switch from the light-mode blue (`#0F74BD`) to the dark-mode brand shade
 * (`#47A6F5` = shade 100), because the darker blue simply disappears.
 */
const darkShadows = {
    shadow2: '0 1px 2px rgba(0,0,0,.28), 0 2px 6px rgba(0,0,0,.32)',
    shadow4: '0 1px 2px rgba(0,0,0,.34), 0 2px 8px rgba(0,0,0,.30)',
    shadow8: '0 2px 4px rgba(0,0,0,.32), 0 10px 24px rgba(0,0,0,.44)',
    shadow16: '0 3px 6px rgba(0,0,0,.34), 0 16px 36px rgba(0,0,0,.50)',
    shadow28: '0 5px 10px rgba(0,0,0,.36), 0 24px 56px rgba(0,0,0,.56)',
    shadow64: '0 8px 16px rgba(0,0,0,.40), 0 44px 96px rgba(0,0,0,.64)',
    shadow2Brand: '0 1px 2px rgba(0,0,0,.30), 0 2px 8px rgba(71,166,245,.14)',
    shadow4Brand: '0 1px 2px rgba(0,0,0,.30), 0 4px 16px rgba(71,166,245,.20)',
    shadow8Brand: '0 2px 6px rgba(0,0,0,.34), 0 8px 24px rgba(71,166,245,.26)',
    shadow16Brand: '0 3px 8px rgba(0,0,0,.36), 0 16px 36px rgba(71,166,245,.32)',
    shadow28Brand: '0 5px 12px rgba(0,0,0,.38), 0 24px 56px rgba(71,166,245,.38)',
    shadow64Brand: '0 8px 18px rgba(0,0,0,.42), 0 44px 96px rgba(71,166,245,.44)',
};
/** Stamps the Nexora radius/elevation/typography tokens onto a generated theme. */
function applyNexoraLook(theme, shadows) {
    return { ...theme, ...nexoraRadius, ...shadows, fontFamilyBase };
}
export const pacxaLightTheme = applyNexoraLook(createLightTheme(pacxaBrand), lightShadows);
const darkTheme = applyNexoraLook(createDarkTheme(pacxaBrand), darkShadows);
/**
 * `createDarkTheme` leaves the two brand foreground tokens pointing at shades
 * chosen for a light background, which reads as under-saturated text on dark.
 * Nudging them one and two steps lighter is the fix the Fluent theme designer
 * emits with every dark theme it generates.
 */
darkTheme.colorBrandForeground1 = pacxaBrand[110];
darkTheme.colorBrandForeground2 = pacxaBrand[120];
export const pacxaDarkTheme = darkTheme;
//# sourceMappingURL=theme.js.map